<template>
    <div class="flex flex-col gap-4 md:gap-5 fs-mask">
        <div class="grid grid-cols-2 gap-2 md:gap-3 lg:grid-cols-4">
            <FormField id="team-search" :label="t('peopleDirectory.searchTeams')" hide-label class="col-span-2">
                <BaseInput
                    v-model="search"
                    type="search"
                    :aria-label="t('peopleDirectory.searchTeams')"
                    :placeholder="t('peopleDirectory.searchTeamsPlaceholder')"
                    @change="logUserInteraction(AmplitudeEventNames.TEAMS_SEARCH_UPDATED, { source: TEAM_INTERACTION_SOURCE.MY_PEOPLE, has_query: !!search.trim() })">
                    <template #prefix><i class="bi bi-search" aria-hidden="true"></i></template>
                </BaseInput>
            </FormField>
            <FormField id="team-filter" :label="t('peopleDirectory.filter')" hide-label>
                <BaseSelect
                    v-model="filter"
                    :aria-label="t('peopleDirectory.filterTeams')"
                    :prefix="t('peopleDirectory.filterPrefix')"
                    :options="filterOptions"
                    dropdown-size="tall"
                    @update:model-value="logUserInteraction(AmplitudeEventNames.TEAMS_FILTER_SELECTED, { source: TEAM_INTERACTION_SOURCE.MY_PEOPLE, team_filter: $event })" />
            </FormField>
            <FormField id="team-sort" :label="t('peopleDirectory.sort')" hide-label>
                <BaseSelect
                    v-model="sort"
                    :aria-label="t('peopleDirectory.sortTeams')"
                    :prefix="t('peopleDirectory.sortPrefix')"
                    :options="sortOptions"
                    dropdown-size="tall"
                    @update:model-value="logUserInteraction(AmplitudeEventNames.TEAMS_SORT_SELECTED, { source: TEAM_INTERACTION_SOURCE.MY_PEOPLE, team_sort: $event })" />
            </FormField>
        </div>
        <p class="scaffold-details-small text-ink-muted order-last">{{ t("peopleDirectory.teamsPrivacy") }}</p>
        <div v-if="!visibleTeams.length && !loading" class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
            <h2 class="body-serif text-ink">{{ teams.length ? t("peopleDirectory.noMatchingTeams") : t("peopleDirectory.noTeams") }}</h2>
            <p class="body-small text-ink-muted mt-1">{{ teams.length ? t("peopleDirectory.adjustTeams") : t("peopleDirectory.noTeamsHelp") }}</p>
            <BaseButton v-if="teams.length" theme="secondary" size="sm" class="mt-4" @click="clearFilters">{{ t("peopleDirectory.clearFilters") }}</BaseButton>
        </div>
        <div v-else class="teams-table-frame" :aria-busy="loading">
            <table class="teams-table" :class="{ 'is-loading': loading }" :aria-label="t('peopleDirectory.teams')">
                <thead>
                    <tr>
                        <th scope="col">{{ t("peopleDirectory.team") }}</th>
                        <th scope="col">{{ t("peopleDirectory.ledBy") }}</th>
                        <th scope="col" class="text-center">{{ t("peopleDirectory.members") }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="!visibleTeams.length" class="teams-table-placeholder">
                        <td colspan="3" />
                    </tr>
                    <tr v-for="team in visibleTeams" :key="team.id">
                        <td>
                            <div class="flex items-center gap-2">
                                <BaseLink
                                    :href="team.url"
                                    class="team-name body-serif text-ink hover:underline"
                                    :before="() => logUserInteraction(AmplitudeEventNames.TEAM_SELECTED, { source: TEAM_INTERACTION_SOURCE.MY_PEOPLE, team_id: team.id })"
                                    >{{ team.name }}</BaseLink
                                >
                                <BaseTooltip v-if="noticesByTeam.has(team.id)" open-on-click :trigger-aria-label="noticeLabel(team.id)">
                                    <template #trigger>
                                        <!-- The span takes the icon-only glyph box so the marble keeps its own size. -->
                                        <BaseButton theme="ghost" size="sm" icon-only class="relative z-10" :aria-label="noticeLabel(team.id)">
                                            <span class="flex items-center justify-center"><NotificationMarble :size="2" /></span>
                                        </BaseButton>
                                    </template>
                                    <template #content>{{ noticeLabel(team.id) }}</template>
                                </BaseTooltip>
                            </div>
                        </td>
                        <td class="team-leads body-small text-ink-muted md:text-ink" :class="{ 'max-md:hidden': !team.leads.length }">
                            <template v-if="team.leads.length">
                                <span class="md:hidden">{{ t("peopleDirectory.teamLedBy", { names: leadNames(team) }) }}</span>
                                <span class="hidden md:inline">{{ leadNames(team) }}</span>
                            </template>
                            <template v-else>
                                <span aria-hidden="true">{{ t("peopleDirectory.emptyCell") }}</span
                                ><span class="sr-only">{{ t("peopleDirectory.noLead") }}</span>
                            </template>
                        </td>
                        <td class="team-members">
                            <div class="team-members-cell">
                                <span class="body-small text-ink whitespace-nowrap md:hidden">{{ t("peopleDirectory.memberCount", { count: team.members.length }) }}</span>
                                <BaseCountBadge :count="team.members.length" :max="999" tone="neutral" :ringed="false" class="team-members-count" />
                                <i class="team-chevron bi bi-chevron-right text-ink-muted" aria-hidden="true" />
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-if="loading" class="teams-table-spinner">
                <span class="table-spinner-badge"><LoadingSpinner class="size-6 text-ink-muted" aria-hidden="true" /></span>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import type { UnviewedTeamMembership } from "~vue/api/generated/product-teams";
import type { PeopleTeamSummary } from "~vue/api/generated/team-perspective/types.gen";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormField from "~vue/components/form/FormField.vue";
import NotificationMarble from "~vue/components/NotificationMarble.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
const {
    teams,
    loading = false,
    viewerId = "",
    unviewedMemberships = [],
} = defineProps<{ teams: PeopleTeamSummary[]; loading?: boolean; viewerId?: string; unviewedMemberships?: UnviewedTeamMembership[] }>();
const search = defineModel<string>("search", { default: "" });
const filter = defineModel<string>("filter", { default: "all" });
const sort = defineModel<string>("sort", { default: "name_asc" });
const { t, locale } = useI18n();
onMounted(() => logUserInteraction(AmplitudeEventNames.TEAMS_VIEWED, { source: TEAM_INTERACTION_SOURCE.MY_PEOPLE }));
const noticesByTeam = computed(() => new Map(unviewedMemberships.map((notice) => [notice.team_id, notice])));
function noticeLabel(teamId: string) {
    const name = noticesByTeam.value.get(teamId)?.invited_by_name;
    return name ? t("peopleDirectory.addedToTeamBy", { name }) : t("peopleDirectory.addedToTeam");
}
const filterOptions = computed(() => [
    { key: "all", label: t("peopleDirectory.all") },
    { key: "lead", label: t("peopleDirectory.teamsYouLead") },
    { key: "member", label: t("peopleDirectory.teamsYouBelongTo") },
]);
const sortOptions = computed(() => [
    { key: "name_asc", label: t("peopleDirectory.nameAsc") },
    { key: "name_desc", label: t("peopleDirectory.nameDesc") },
]);
// The viewer reads as "You", listed first, rather than as their own name.
const leadNames = (team: PeopleTeamSummary) =>
    [...team.leads]
        .sort((a, b) => Number(b.id === viewerId) - Number(a.id === viewerId))
        .map((lead) => (lead.id === viewerId ? t("peopleDirectory.leadYou") : lead.name))
        .join(", ");
const visibleTeams = computed(() => {
    const terms = search.value.toLocaleLowerCase(locale.value).trim().split(/\s+/);
    return teams
        .filter((team) => {
            if (filter.value === "lead" && !team.is_lead) {
                return false;
            }
            if (filter.value === "member" && team.is_lead) {
                return false;
            }
            const content = [team.name, ...team.members.map((member) => member.name)].join(" ").toLocaleLowerCase(locale.value);
            return terms.every((term) => content.includes(term));
        })
        .sort((a, b) => a.name.localeCompare(b.name, locale.value) * (sort.value === "name_desc" ? -1 : 1));
});
function clearFilters() {
    logUserInteraction(AmplitudeEventNames.TEAMS_FILTER_SELECTED, { source: TEAM_INTERACTION_SOURCE.MY_PEOPLE, team_filter: "all" });
    search.value = "";
    filter.value = "all";
}
</script>
<style scoped>
@reference "../../../css/tailwind.css";
@layer components {
    .teams-table-frame {
        @apply relative;
    }
    /* Holds the frame open while a first load has no rows to show yet. */
    .teams-table tr.teams-table-placeholder td {
        @apply col-span-full block h-64 md:table-cell md:w-auto;
    }
    .teams-table.is-loading tbody {
        @apply opacity-40 pointer-events-none;
    }
    .teams-table-spinner {
        @apply absolute inset-0 flex items-center justify-center;
    }
    .table-spinner-badge {
        @apply flex rounded-full border border-border bg-surface-raised p-2 shadow-sm;
    }
    .teams-table {
        @apply w-full border-separate border-spacing-0 rounded-lg border border-border bg-surface-raised;
    }
    .teams-table thead {
        @apply hidden md:table-header-group;
    }
    .teams-table th {
        @apply scaffold-details-small text-ink-muted text-left px-5 py-3 border-b border-border;
    }
    .teams-table tbody {
        @apply block md:table-row-group;
    }
    .teams-table tr {
        @apply relative grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 px-4 py-3 min-h-17 border-b border-border last:border-b-0 md:table-row md:p-0;
    }
    .teams-table td {
        @apply min-w-0 md:table-cell md:px-5 md:py-4 md:border-b md:border-border;
    }
    .team-name::after {
        content: "";
        @apply absolute inset-0;
    }
    .teams-table th:first-child,
    .teams-table td:first-child {
        @apply md:w-1/2;
    }
    /* On mobile the lead line sits under the team name and the member count spans both rows. */
    .team-leads {
        @apply col-start-1;
    }
    .team-members {
        @apply col-start-2 row-span-2 row-start-1;
    }
    /* Desktop centres the count chip in the column with the chevron pinned to the right edge. */
    .team-members-cell {
        @apply flex items-center justify-between gap-2 md:grid md:grid-cols-[1fr_auto_1fr];
    }
    .team-members-count {
        @apply hidden md:col-start-2 md:inline-flex;
    }
    .team-chevron {
        @apply md:col-start-3 md:justify-self-end md:opacity-0;
    }
    .teams-table tr:hover .team-chevron,
    .teams-table tr:focus-within .team-chevron {
        @apply opacity-100;
    }
    .teams-table tbody tr:last-child td {
        @apply border-b-0;
    }
    .teams-table tbody tr {
        @apply hover:bg-surface-sunken focus-within:bg-surface-sunken;
    }
}
</style>
